/**
 * API client pour le module StockOut (Sorties de Stock)
 */
import axios from 'axios';
import {
  StockOut,
  CreateStockOutDto,
  CreateStockOutBatchDto,
  StockOutBatchResult,
  StockOutFilter,
  StockOutListResult,
  DeleteStockOutResult,
  LossesReport,
  PersonalUseReport,
  ComptagesFicheResult,
} from '../types/stock-out.types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/**
 * Récupère le tenant ID depuis localStorage
 */
function getTenantId(): string | null {
  try {
    const tenantData = localStorage.getItem('tenant');
    if (tenantData) {
      const tenant = JSON.parse(tenantData);
      return tenant?.id || null;
    }
  } catch (e) {
    console.error('Failed to parse tenant data:', e);
  }
  return null;
}

/**
 * Récupère le token d'authentification depuis localStorage
 */
function getAuthToken(): string | null {
  const customerToken = localStorage.getItem('customer_token');
  const staffToken = localStorage.getItem('auth_token');
  return customerToken || staffToken;
}

/**
 * Crée les headers avec X-Tenant-ID et Authorization
 */
function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const tenantId = getTenantId();
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }

  return headers;
}

export const stockOutApi = {
  /**
   * Créer une sortie de stock
   */
  async create(data: CreateStockOutDto): Promise<StockOut> {
    const response = await fetch(`${API_BASE_URL}/stock-out`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la création de la sortie');
    }

    return response.json();
  },

  /**
   * Sortir tout un panier en stock (perte / prélèvement par panier).
   * Résolution FEFO des lots côté serveur.
   */
  async createBatch(data: CreateStockOutBatchDto): Promise<StockOutBatchResult> {
    const response = await fetch(`${API_BASE_URL}/stock-out/batch`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la sortie du panier');
    }

    return response.json();
  },

  /**
   * Lister les sorties de stock avec filtres optionnels
   */
  async list(filters?: StockOutFilter): Promise<StockOutListResult> {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.productId) params.append('productId', filters.productId);
    if (filters?.limit != null) params.append('limit', String(filters.limit));
    if (filters?.offset != null) params.append('offset', String(filters.offset));

    const url = `${API_BASE_URL}/stock-out${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des sorties');
    }

    return response.json();
  },

  /**
   * Récupérer une sortie de stock par ID
   */
  async get(id: string): Promise<StockOut> {
    const response = await fetch(`${API_BASE_URL}/stock-out/${id}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération de la sortie');
    }

    return response.json();
  },

  /**
   * Supprimer une sortie de stock
   */
  async delete(id: string): Promise<DeleteStockOutResult> {
    const response = await fetch(`${API_BASE_URL}/stock-out/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Erreur lors de la suppression');
    }

    return response.json();
  },

  /**
   * Rapport de pertes
   */
  async getLossesReport(startDate: string, endDate: string): Promise<LossesReport> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await fetch(`${API_BASE_URL}/stock-out/report/losses?${params.toString()}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération du rapport de pertes');
    }

    return response.json();
  },

  /**
   * Rapport des prélèvements personnels (pour TVA)
   */
  async getPersonalUseReport(startDate: string, endDate: string): Promise<PersonalUseReport> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await fetch(`${API_BASE_URL}/stock-out/report/personal?${params.toString()}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération du rapport de prélèvements');
    }

    return response.json();
  },

  /**
   * Comptages « Compter ce produit » à la baisse de la période, un par un
   * (annulés compris). L'annulation passe par `posApi.annulerComptage`.
   */
  async listComptages(startDate: string, endDate: string): Promise<ComptagesFicheResult> {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await fetch(`${API_BASE_URL}/stock-out/comptages?${params.toString()}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des comptages');
    }

    return response.json();
  },

  /**
   * Résumé quotidien (pour rapport Z)
   */
  async getDailySummary(date?: string): Promise<{
    date: string;
    total_count: number;
    total_cost_cents: number;
    total_sale_cents: number;
    by_type: Array<{ type: string; count: number; total_cents: number }>;
  }> {
    const params = date ? `?date=${date}` : '';
    const response = await fetch(`${API_BASE_URL}/stock-out/report/daily-summary${params}`, {
      headers: getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération du résumé quotidien');
    }

    return response.json();
  },

  /**
   * Upload une photo pour prouver une sortie de stock (perte, casse, etc.)
   */
  async uploadPhoto(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);

    const token = getAuthToken();
    const tenantId = getTenantId();

    const response = await axios.post(`${API_BASE_URL}/uploads/image`, formData, {
      headers: {
        ...(tenantId && { 'X-Tenant-ID': tenantId }),
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.data.url) {
      throw new Error('URL de l\'image non reçue');
    }

    return response.data.url;
  },
};
